<template>
  <div class="irp-partner-filter-bar" data-testid="irp-partner-filter-bar">
    <h2 class="irp-partner-filter-bar__heading">Partner data</h2>
    <div class="irp-partner-filter-bar__controls">
      <BittsQuickFilter
        v-model="partnerSelection"
        :filter="PARTNER_FILTER"
        :options="partnerOptions"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-partner"
        @apply-filter="applyPartnerFilter"
        @clear-filter="clearPartnerFilter"
        @remove-filter="(reload) => reload && clearPartnerFilter()"
      >
        <template #option-prefix-icon="{ item }">
          <BittsAvatar :org="orgForValue(item.value)" size="x-small" />
        </template>
      </BittsQuickFilter>
      <BittsQuickFilter
        v-model="segmentSelection"
        :filter="SEGMENT_FILTER"
        :options="SEGMENT_OPTIONS"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-population"
        @apply-filter="applySegmentFilter"
        @clear-filter="clearSegmentFilter"
        @remove-filter="(reload) => reload && clearSegmentFilter()"
      />
      <BittsQuickFilter
        v-model="scoreSelection"
        :filter="SCORE_FILTER"
        :options="SCORE_BUCKET_OPTIONS"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-score"
        @apply-filter="applyScoreFilter"
        @clear-filter="clearScoreFilter"
        @remove-filter="(reload) => reload && clearScoreFilter()"
      />
      <BittsQuickFilter
        v-model="tagSelection"
        :filter="TAGS_FILTER"
        :options="tagOptions"
        :open-on-mount="false"
        :no-custom-value="true"
        size="medium"
        data-testid="irp-partner-filter-bar-tags"
        @apply-filter="applyTagFilter"
        @clear-filter="clearTagFilter"
        @remove-filter="(reload) => reload && clearTagFilter()"
      />
      <BittsButton
        :center-icon="expandToggleIcon"
        :aria-label="expandToggleLabel"
        type="neutral"
        variant="outline"
        size="small"
        data-testid="irp-partner-filter-bar-expand-toggle"
        @click="toggleExpansion"
      />
      <BittsInput
        :model-value="partnerSearch"
        :expand-on-focus="true"
        placeholder="Search"
        name="partner-search"
        type="search"
        class="irp-partner-filter-bar__search"
        data-testid="irp-partner-filter-bar-search"
        @update:model-value="onPartnerSearchUpdated"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsInput,
  BittsQuickFilter,
} from '@crossbeam/bitts';
import type { BittsFilterProp } from '@crossbeam/bitts';

import { debounce } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref } from 'vue';

import { useIndividualRecordStore, usePartnersStore } from '@/stores';
import type { RecordSectionType } from '@/types/individual_record';
import type { Partner, PartnerScoreBucket, PartnerTag } from '@/types/partners';
import type { SegmentType } from '@/types/populations';

const { section } = defineProps<{ section: RecordSectionType }>();

const emit = defineEmits<{
  'toggle-expansion': [isExpanded: boolean];
}>();

const SEARCH_DEBOUNCE_MS = 300;

const PARTNER_FILTER: BittsFilterProp = {
  label: 'Partner',
  value: 'partner',
  faIcon: ['fak', 'partner'],
  datatype: 'text',
  useSearch: true,
};

const SEGMENT_FILTER: BittsFilterProp = {
  label: 'Populations',
  value: 'populations',
  faIcon: ['fak', 'population'],
  datatype: 'text',
  useSearch: true,
};

const SCORE_FILTER: BittsFilterProp = {
  label: 'Score',
  value: 'partner_score',
  faIcon: ['fak', 'partner-score'],
  datatype: 'text',
  useSearch: false,
};

const TAGS_FILTER: BittsFilterProp = {
  label: 'Tags',
  value: 'partner_tags',
  faIcon: ['fak', 'tag'],
  datatype: 'text',
  useSearch: true,
};

const SCORE_BUCKET_OPTIONS = [
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const SEGMENT_OPTIONS = [
  { label: 'Customers', value: 'customers' },
  { label: 'Prospects', value: 'prospects' },
  { label: 'Open Opportunities', value: 'open_opportunities' },
  { label: 'Other', value: 'other' },
];

const individualRecordStore = useIndividualRecordStore();
const partnersStore = usePartnersStore();
const { partnerOrgs, partnerTags } = storeToRefs(partnersStore);

const sectionFilters = computed(
  () => individualRecordStore.sections[section].filters,
);

const partnerOptions = computed(() =>
  partnerOrgs.value.map((org: Partner) => ({
    label: org.name,
    value: org.id,
  })),
);

function orgForValue(value: string | number) {
  return partnersStore.getPartnerOrgById(Number(value));
}

const tagOptions = computed(() =>
  partnerTags.value
    .filter((tag: PartnerTag) => tag.label)
    .map((tag: PartnerTag) => ({
      label: tag.label,
      value: tag.id as string | number,
    })),
);

const partnerSelection = ref<(string | number)[]>([
  ...sectionFilters.value.partner_org_ids,
]);
function applyPartnerFilter() {
  individualRecordStore.setFilter(
    section,
    'partner_org_ids',
    partnerSelection.value.map(Number),
  );
}
function clearPartnerFilter() {
  partnerSelection.value = [];
  individualRecordStore.setFilter(section, 'partner_org_ids', []);
}

const segmentSelection = ref<(string | number)[]>([
  ...sectionFilters.value.partner_segments,
]);
function applySegmentFilter() {
  individualRecordStore.setFilter(
    section,
    'partner_segments',
    segmentSelection.value as SegmentType[],
  );
}
function clearSegmentFilter() {
  segmentSelection.value = [];
  individualRecordStore.setFilter(section, 'partner_segments', []);
}

const scoreSelection = ref<(string | number)[]>([
  ...sectionFilters.value.partner_score_bucket,
]);
function applyScoreFilter() {
  individualRecordStore.setFilter(
    section,
    'partner_score_bucket',
    scoreSelection.value as NonNullable<PartnerScoreBucket>[],
  );
}
function clearScoreFilter() {
  scoreSelection.value = [];
  individualRecordStore.setFilter(section, 'partner_score_bucket', []);
}

const tagSelection = ref<(string | number)[]>([
  ...sectionFilters.value.partner_tag_ids,
]);
function applyTagFilter() {
  individualRecordStore.setFilter(
    section,
    'partner_tag_ids',
    tagSelection.value as unknown as number[],
  );
}
function clearTagFilter() {
  tagSelection.value = [];
  individualRecordStore.setFilter(section, 'partner_tag_ids', []);
}

const partnerSearch = ref(sectionFilters.value.partner_search);
const setPartnerSearch = debounce(
  (value: string) =>
    individualRecordStore.setFilter(section, 'partner_search', value),
  SEARCH_DEBOUNCE_MS,
);
onBeforeUnmount(() => setPartnerSearch.cancel());
function onPartnerSearchUpdated(value: string) {
  partnerSearch.value = value;
  setPartnerSearch(value);
}

const isExpanded = ref(false);
const expandToggleIcon = computed<['fak', string]>(() =>
  isExpanded.value ? ['fak', 'collapse-all'] : ['fak', 'expand-all'],
);
const expandToggleLabel = computed(() =>
  isExpanded.value ? 'Collapse all' : 'Expand all',
);
function toggleExpansion() {
  isExpanded.value = !isExpanded.value;
  emit('toggle-expansion', isExpanded.value);
}
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-partner-filter-bar {
  @apply flex flex-col gap-8;
}

.irp-partner-filter-bar__heading {
  @apply m-0 text-m font-semibold text-neutral-text-strong;
}

.irp-partner-filter-bar__controls {
  @apply flex flex-wrap items-center gap-8;
}

.irp-partner-filter-bar__search {
  @apply ml-auto w-[240px];
}
</style>
